'use client';

import { setSession } from '@/utils/tools';
import { cva } from 'class-variance-authority';
import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import { useSpmDomAttr } from '@/reporting/spm';
import Link from 'next/link';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { getSpmPositionCode, useProductDetailSpmCodes } from '../hooks/useGoodsDetailTrack';

interface RelationItemProps {
  item: RelationItem;
  product: any;
  index: number;
  position?: number;
  isWeb?: boolean;
  onRelatedClick?: (item: RelationItem, index: number) => void;
  onRelatedExposure?: (item: RelationItem, index: number) => void;
}

export interface RelationItem {
  id: number | string;
  shop_sku: string;
  short_name: string;
  image: string;
  image_gray?: string;
  qty: number;
  status: number;
  is_glass?: number;
  is_smart?: number;
  is_goggles?: number;
  is_new?: number;
}

export const RelationItem: React.FC<RelationItemProps> = ({
  item,
  product,
  index,
  position,
  isWeb = false,
  onRelatedClick,
  onRelatedExposure,
}) => {
  const t = useTranslations('goodsDetail');
  // 关联品（颜色变体）坑位 Data SPM DOM 打标：与 onRelatedClick 上报同源同值——
  // 同一 index 由 useGoodsDetailTrack 解析 select_variant_NN（2 位补零）；
  // 翻页后继续使用完整列表索引，不重置序号。
  const codes = useProductDetailSpmCodes(isWeb);
  const relatedDomSpm = useSpmDomAttr(
    onRelatedClick || onRelatedExposure
      ? getSpmPositionCode(
          codes.color_variants,
          `select_variant_${String(index + 1).padStart(2, '0')}`,
        )
      : undefined,
  );
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onRelatedExposure?.(item, index);
    }
  }, [inView]);

  // 判断是否为灰色图片
  const isGrayImage = (_item: RelationItem) => {
    return !!_item.image_gray;
  };

  // CVA 商品容器样式变体
  const itemVariants = cva(
    'relation-item relative box-border cursor-pointer transition-all duration-300',
    {
      variants: {
        device: {
          web: `flex items-center justify-center w-[72px] h-[72px] rounded-[4px] border ${item.is_glass === 1 ? 'p-[26px_7px_15px_7px]' : ''}`,
          mobile: 'block w-[62px] h-[52px] rounded-[4px] overflow-hidden border',
        },
        imageType: {
          normal: '',
          color: 'rounded-full overflow-hidden p-0 bg-transparent w-[28px] h-[28px]',
        },
        borderState: {
          transparent: 'border-transparent border-solid',
          selected: '!border-[#808080] border-solid',
          soldOut: 'border-dashed border-[rgba(40,40,40,0.4)]',
        },
        backgroundState: {
          soldOut: 'bg-[rgba(238,238,238,0.4)]',
          selected: 'bg-[#eeeeee]',
          normal: 'bg-[#eeeeee]',
        },

        hover: {
          enabled: 'hover:border-[#d4d4d4] hover:border-solid',
          disabled: '',
        },
        itemType: {
          normal: '',
          accessory: 'acc-item !p-0',
          mobileAccessory: 'acc-item',
        },
        correlationStyle: {
          normal: '',
          threeFourth: 'three-fourth p-[23px_0_7px_7px]',
          half: 'half p-[16px_0_0_16px]',
        },
      },
      defaultVariants: {
        device: 'mobile',
        imageType: 'normal',
        borderState: 'transparent',
        backgroundState: 'normal',
        hover: isWeb ? 'enabled' : 'disabled',
        itemType: 'normal',
        correlationStyle: 'normal',
      },
    },
  );
  const isSelected = String(item.id) === String(product.id);
  const relatedItemImageAlt = `${item.short_name}${item.is_glass === 1 ? ' glasses' : ''} ${(position ?? index) + 1}`;
  // 获取商品样式类名
  const getItemClasses = () => {
    const isSoldOut = item.qty <= 0;
    const isAccessory = item.is_glass === 2 || item.is_smart === 1 || item.is_goggles === 1;

    let borderState: any = 'transparent';
    let backgroundState: any = 'normal';

    if (isSelected) {
      borderState = 'selected';
      backgroundState = 'selected';
    } else if (isSoldOut) {
      borderState = 'soldOut';
      backgroundState = 'soldOut';
    }

    return itemVariants({
      device: isWeb ? 'web' : 'mobile',
      borderState,
      backgroundState,
      hover: isWeb ? 'enabled' : 'disabled',
      itemType: isAccessory ? 'mobileAccessory' : 'normal',
    });
  };

  // CVA 图片样式变体
  const imageVariants = cva('block', {
    variants: {
      device: {
        web: 'w-full bg-[#eeeeee]',
        mobile: 'w-full h-auto object-contain',
      },
      blendMode: {
        normal: 'mix-blend-normal',
        multiply: 'mix-blend-multiply',
      },
      opacity: {
        normal: 'opacity-100',
        reduced: 'opacity-40',
      },
      imageType: {
        color: 'w-full h-full rounded-full box-border object-cover',
        normal: '',
      },
      imageStyle: {
        normal: '',
        ratio: 'w-auto h-full absolute left-0 top-0 object-contain',
      },
      itemType: {
        normal: '',
        accessory: '!w-full object-contain',
        mobileAccessory: 'w-full h-full object-contain',
      },
    },
    defaultVariants: {
      device: 'web',
      blendMode: 'normal',
      opacity: 'normal',
      imageType: 'normal',
      imageStyle: 'normal',
      itemType: 'normal',
    },
  });

  // 获取图片类名
  const getImageClasses = () => {
    // 移动端逻辑
    if (!isWeb) {
      const isAccessory = item.is_glass === 2 || item.is_smart === 1 || item.is_goggles === 1;
      return imageVariants({
        device: 'mobile',
        itemType: isAccessory ? 'mobileAccessory' : 'normal',
      });
    }

    // PC端逻辑
    const isAccessory = item.is_glass === 2 || item.is_smart === 1 || item.is_goggles === 1;
    const isColorType = product.correlation_image_type === 'color';
    const isRatioStyle =
      product.correlation_image_style === '3-4' || product.correlation_image_style === '1-2';
    const isGray = isGrayImage(item);
    const isSoldOut = item.qty <= 0;

    // 颜色模式下的特殊样式
    if (isColorType) {
      return imageVariants({ imageType: 'color' });
    }

    // 确定混合模式
    let blendMode: 'normal' | 'multiply' = 'normal';
    if (!isAccessory && !isGray) {
      blendMode = 'multiply';
    }

    return imageVariants({
      device: 'web',
      blendMode,
      opacity: isSoldOut ? 'reduced' : 'normal',
      imageStyle: isRatioStyle ? 'ratio' : 'normal',
      itemType: isAccessory ? 'accessory' : 'normal',
    });
  };

  // 获取图片样式
  const getImageStyle = () => {
    if (!isWeb) {
      const baseStyle: React.CSSProperties = {};
      if (item.qty <= 0) {
        baseStyle.opacity = 0.4;
      }
      if (item.is_glass === 2 || item.is_smart === 1 || item.is_goggles === 1) {
        baseStyle.mixBlendMode = 'normal';
      } else if (!isGrayImage(item)) {
        baseStyle.mixBlendMode = 'multiply';
      }
      return baseStyle;
    }
    return {};
  };

  // 处理商品点击
  const handleItemClick = () => {
    setSession('zl_goodsDetailEnterPageScene', '商品详情页关联品坑位');
    onRelatedClick?.(item, index);
  };

  const getContainerStyle = () => {
    if (!isWeb) {
      const containerClasses =
        'w-full h-full relative flex items-center justify-center rounded-[3px]';
      let paddingClasses = 'pt-[15px] px-[6px] pb-[11px]';
      if (item.is_glass === 2 || item.is_smart === 1 || item.is_goggles === 1) {
        paddingClasses = 'p-0';
      }
      return `${containerClasses} ${paddingClasses}`;
    }
    return 'block w-full h-full';
  };

  return (
    <Link
      ref={ref}
      href={`/goods-detail/${item.shop_sku}`}
      prefetch={false}
      className={getItemClasses()}
      aria-current={isSelected ? 'page' : undefined}
      onClick={handleItemClick}
      {...relatedDomSpm}
    >
      <div className={`${getContainerStyle()} relative`}>
        <div className="w-full h-full rounded-[inherit] outline-none">
          {isWeb && product.correlation_image_type === 'color' ? (
            <div className="border border-[#eeeeee] rounded-full box-border bg-white w-full h-full flex items-center justify-center pointer-events-none">
              <Image
                className={getImageClasses()}
                src={item.image_gray || item.image}
                alt={relatedItemImageAlt}
                width={28}
                height={28}
                sizes="28px"
              />
            </div>
          ) : (
            <div className="w-full h-full rounded-[3px] overflow-hidden flex justify-center pointer-events-none">
              <Image
                className={getImageClasses()}
                src={item.image_gray || item.image}
                alt={relatedItemImageAlt}
                style={getImageStyle()}
                width={isWeb ? 72 : 100}
                height={isWeb ? 36 : 50}
                sizes={isWeb ? '72px' : '(max-width: 768px) 50px, 100px'}
              />
            </div>
          )}
        </div>
      </div>

      {/* 状态标签 */}
      {item.status === 1 && product.correlation_image_type !== 'color' && (
        <>
          {item.qty <= 0 && (
            <span
              className={`absolute top-0 right-0 ${
                isWeb
                  ? 'min-w-[63px] h-[20px] text-[12px] leading-[12px]'
                  : 'min-w-[50px] h-[14px] text-[10px] leading-[14px]'
              } bg-[#d4d4d4] text-[#808080] text-center rounded-tr-[4px] rounded-bl-[4px] flex items-center justify-center font-normal`}
            >
              {t('SoldOut')}
            </span>
          )}
          {item.qty > 0 && item.is_new === 1 && (
            <span
              className={`absolute top-0 right-0 ${
                isWeb
                  ? 'min-w-[40px] h-[20px] p-[0_5px] bg-[#c7ff57] text-[12px] leading-[12px]'
                  : 'min-w-[29px] h-[14px] bg-[#daff91] text-[10px] leading-[14px]'
              } text-[#282828] text-center rounded-tr-[4px] rounded-bl-[4px] flex items-center justify-center font-normal`}
            >
              {t('New')}
            </span>
          )}
        </>
      )}
    </Link>
  );
};

export default RelationItem;
